import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { CalendarClock, RefreshCw, MapPin, Users, ExternalLink, Video } from "lucide-react";
import { Button } from "@/components/ui/button";

// ============================================================================
// Hiring Calendar — Phase F UI (2026-08-24 pt.26).
// Server-side rendered list of upcoming interviews (grouped by day).
// No iframe — native render so Katt/Juliana see details without a
// Google session. Depends on HIRING_CALENDAR_ENABLED=true + OAuth.
// ============================================================================

function DateHeader({ d }) {
  return (
    <div className="sticky top-0 bg-[#f9fafb] px-4 py-2 border-y border-[#e5e7eb] text-xs font-semibold uppercase tracking-wide text-[#4b5563]">
      {new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
    </div>
  );
}

function groupByDay(rows) {
  const map = new Map();
  for (const r of rows) {
    const key = new Date(r.scheduled_at).toISOString().slice(0, 10);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

export default function HiringCalendar() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: null });
  const [rows, setRows] = useState([]);
  const [cal, setCal] = useState(null);

  async function load() {
    setState({ loading: true, error: null });
    try {
      const [list, status] = await Promise.all([
        api.get("/hiring/calendar/interviews"),
        api.get("/hiring/calendar/status"),
      ]);
      setRows(list.data?.interviews || []);
      setCal(status.data);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: extractApiError(e) });
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const empty = !state.loading && !state.error && rows.length === 0;
  const days = groupByDay(rows);

  return (
    <div className="space-y-6" data-testid="hiring-calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0e1420]">Hiring — Calendar</h1>
          <p className="text-sm text-[#4b5563] mt-1">Upcoming Stage 4 interviews from the shared Google Calendar.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="hiring-calendar-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {cal && !cal.enabled && (
        <div className="bg-[#fef3c7] border border-[#fbbf24] rounded-lg p-3 text-sm text-[#78350f]" data-testid="hiring-calendar-flag-off">
          Calendar integration is not yet enabled (HIRING_CALENDAR_ENABLED=false). Interviews cannot be scheduled from the app until an admin turns this on.
        </div>
      )}
      {cal && cal.enabled && !cal.connected && (
        <div className="bg-[#fef3c7] border border-[#fbbf24] rounded-lg p-3 text-sm text-[#78350f]" data-testid="hiring-calendar-not-connected">
          Google Calendar is not connected yet.{" "}
          <Link to={`/${slug}/app/hiring/settings`} className="underline">Connect it in Hiring Settings</Link>.
        </div>
      )}

      <PanelState
        status={state.loading ? "loading" : state.error ? "error" : "ok"}
        error={state.error}
        isEmpty={empty}
        onRetry={load}
        emptyTitle="No upcoming interviews"
        emptyBody="Schedule interviews from a Stage 4 candidate's detail page."
        testIdPrefix="hiring-calendar"
      />

      {!state.loading && !state.error && rows.length > 0 && (
        <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden" data-testid="hiring-calendar-list">
          {days.map(([day, items]) => (
            <div key={day}>
              <DateHeader d={day} />
              {items.map((iv) => (
                <div key={iv.id} className="px-4 py-3 border-b border-[#f3f4f6] last:border-b-0" data-testid={`hiring-calendar-event-${iv.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[#0e1420]">
                      {new Date(iv.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      <span className="text-[#4b5563] font-normal ml-2">· {iv.duration_minutes} min</span>
                    </div>
                    <Link to={`/${slug}/app/hiring/candidates/${iv.candidate_id}`} className="text-xs text-[#2563eb] hover:underline inline-flex items-center gap-1">
                      Candidate <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="text-xs text-[#4b5563] mt-1 flex flex-wrap gap-x-4">
                    {iv.attendee_emails?.length > 0 && (
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{iv.attendee_emails.join(", ")}</span>
                    )}
                    {iv.location_or_link && (
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{iv.location_or_link}</span>
                    )}
                    {iv.meet_link && (
                      <a
                        href={iv.meet_link}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#2563eb] hover:underline"
                        data-testid={`hiring-calendar-meet-${iv.id}`}
                      >
                        <Video className="w-3 h-3" /> Join Meet
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
